import React, { useState, useRef, useEffect } from "react";
import { Button, Checkbox } from "@legion-ui/core";
import { useApiTags } from "@/hooks/useApiTags";
import { useApiPublishers } from "@/hooks/useApiPublishers";
import { capitalizeFirstLetter } from "@/utils/capitalizeFirstLetter";

interface FilterValues {
  publisher: string;
  category: string[];
  typePlan: string[];
  status: string[];
}

interface Props {
  open: boolean;
  onClose: () => void;
  onApply: (filters: FilterValues) => void;
  initialValues: FilterValues;
}

export const ModalFilter: React.FC<Props> = ({
  open,
  onClose,
  onApply,
  initialValues,
}) => {
  const categoryRef = useRef<HTMLDivElement | null>(null);

  const { tags, isLoading } = useApiTags();
  const {
    data: publishersData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading: isLoadingPublishers,
  } = useApiPublishers();

  /* =======================
   * STATE
   ======================= */
  const [publisher, setPublisher] = useState("");
  const [category, setCategory] = useState<string[]>([]);
  const [categoryOpen, setCategoryOpen] = useState(false);
  const [publisherOpen, setPublisherOpen] = useState(false);

  const [typePlan, setTypePlan] = useState({
    free: false,
    prepaid: false,
    postpaid: false,
  });

  const [status, setStatus] = useState({
    ACTIVE: false,
    INACTIVE: false,
  });

  const publishers = publishersData?.pages.flatMap((p) => p.items) ?? [];

  /* =======================
   * INIT FROM QUERY PARAMS
   ======================= */
  useEffect(() => {
    if (!open) return;

    setPublisher(initialValues.publisher || "");
    setCategory(initialValues.category || []);

    setTypePlan({
      free: initialValues.typePlan.includes("free"),
      prepaid: initialValues.typePlan.includes("prepaid"),
      postpaid: initialValues.typePlan.includes("postpaid"),
    });

    setStatus({
      ACTIVE: initialValues.status.includes("ACTIVE"),
      INACTIVE: initialValues.status.includes("INACTIVE"),
    });
  }, [initialValues, open]);

  /* =======================
   * CLICK OUTSIDE CATEGORY
   ======================= */
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        categoryOpen &&
        categoryRef.current &&
        !categoryRef.current.contains(e.target as Node)
      ) {
        setCategoryOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [categoryOpen]);

  /* =======================
   * HANDLERS
   ======================= */
  const handlePublisherScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 20) {
      if (hasNextPage && !isFetchingNextPage) fetchNextPage();
    }
  };

  const toggleCategory = (tag: string) => {
    setCategory((prev) =>
      prev.includes(tag) ? prev.filter((x) => x !== tag) : [...prev, tag]
    );
  };

  const selectAllCategory = () => setCategory(tags);
  const unselectAllCategory = () => setCategory([]);

  const resetFilters = () => {
    setPublisher("");
    setCategory([]);
    setTypePlan({ free: false, prepaid: false, postpaid: false });
    setStatus({ ACTIVE: false, INACTIVE: false });
  };

  const apply = () => {
    onApply({
      publisher,
      category,
      typePlan: Object.keys(typePlan).filter((p) => typePlan[p]),
      status: Object.keys(status).filter((s) => status[s]),
    });

    setCategoryOpen(false);
    setPublisherOpen(false);
    onClose();
  };

  if (!open) return null;

  /* =======================
   * RENDER
   ======================= */
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-lg shadow-lg w-[600px] max-h-[80vh] overflow-y-auto">

        {/* HEADER */}
        <div className="p-4 border-b">
          <h2 className="text-lg font-semibold">Filter API</h2>
        </div>

        {/* BODY */}
        <div className="p-6">
          <div className="grid grid-cols-2 gap-6">

            {/* PUBLISHER */}
            <div>
              <label className="mb-1 text-sm font-semibold block">Publisher</label>
              <div className="relative">
                <div
                  className="border rounded px-3 py-2 cursor-pointer"
                  onClick={() => setPublisherOpen((p) => !p)}
                >
                  {publisher || <span className="text-gray-400">- Select -</span>}
                </div>

                {publisherOpen && (
                  <div
                    className="absolute z-50 mt-1 w-full bg-white border rounded shadow-lg max-h-[200px] overflow-auto"
                    onScroll={handlePublisherScroll}
                  >
                    {/* CLEAR OPTION */}
                    <div
                      onClick={() => {
                        setPublisher("");
                        setPublisherOpen(false);
                      }}
                      className={`px-3 py-2 cursor-pointer hover:bg-gray-100 text-gray-600 ${
                        publisher === "" ? "bg-blue-50 font-semibold" : ""
                        }`}
                    >
                      All Publisher
                    </div>

                    {isLoadingPublishers ? (
                      <div className="p-2 text-sm text-gray-400">Loading...</div>
                    ) : (
                      publishers.map((p) => (
                        <div
                          key={p}
                          onClick={() => {
                            setPublisher(p);
                            setPublisherOpen(false);
                          }}
                          className={`px-3 py-2 cursor-pointer hover:bg-gray-100 ${publisher === p ? "bg-blue-50 font-semibold" : ""
                            }`}
                        >
                          {p}
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* CATEGORY */}
            <div>
              <label className="mb-1 text-sm font-semibold block">Category</label>

              <div className="relative" ref={categoryRef}>
                <div
                  className="border rounded px-3 py-2 cursor-pointer"
                  onClick={() => setCategoryOpen((o) => !o)}
                >
                  {category.length
                    ? `${category.length} selected`
                    : <span className="text-gray-400">- Select -</span>}
                </div>

                {categoryOpen && (
                  <div className="absolute z-50 mt-1 w-full bg-white border rounded shadow-lg p-3 max-h-[200px] overflow-auto">
                    {isLoading ? (
                      <div className="text-sm text-gray-400">Loading...</div>
                    ) : (
                      <>
                        <label className="flex gap-2 mb-2">
                          <Checkbox
                            checked={category.length === tags.length}
                            onChange={(e) =>
                              (e.target as HTMLInputElement).checked
                                ? selectAllCategory()
                                : unselectAllCategory()
                            }
                          />
                          <span className="font-medium">Select All</span>
                        </label>

                        {tags.map((t) => (
                          <label key={t} className="flex gap-2 py-1">
                            <Checkbox
                              checked={category.includes(t)}
                              onChange={() => toggleCategory(t)}
                            />
                            <span>{t}</span>
                          </label>
                        ))}
                      </>
                    )}
                  </div>
                )}
              </div>

              {category.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {category.map((c) => (
                    <span
                      key={c}
                      className="px-2 py-1 bg-blue-600 text-white rounded-full text-sm flex items-center gap-2"
                    >
                      {c}
                      <button onClick={() => toggleCategory(c)}>✕</button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* TYPE PLAN */}
            <div>
              <label className="text-sm font-semibold mb-1 block">Type Plan</label>
              {["free", "prepaid", "postpaid"].map((p) => (
                <label key={p} className="flex gap-2">
                  <Checkbox
                    checked={typePlan[p]}
                    onChange={(e) =>
                      setTypePlan({ ...typePlan, [p]: (e.target as HTMLInputElement).checked })
                    }
                  />
                  {capitalizeFirstLetter(p)}
                </label>
              ))}
            </div>

            {/* STATUS */}
            <div>
              <label className="text-sm font-semibold mb-1 block">API Status</label>
              {["ACTIVE", "INACTIVE"].map((s) => (
                <label key={s} className="flex gap-2">
                  <Checkbox
                    checked={status[s]}
                    onChange={(e) =>
                      setStatus({ ...status, [s]: (e.target as HTMLInputElement).checked })
                    }
                  />
                  {s}
                </label>
              ))}
            </div>
          </div>

          <button className="mt-6 text-sm font-semibold" onClick={resetFilters}>
            Reset all filters
          </button>
        </div>

        {/* FOOTER */}
        <div className="p-4 border-t flex justify-end gap-3">
          <Button variant="transparent" onClick={onClose}>Cancel</Button>
          <Button color="primary" variant="solid" onClick={apply}>Apply Filter</Button>
        </div>
      </div>
    </div>
  );
};
